import React, { useEffect, useState, useRef, useContext } from 'react';
import Context from '../../../context/context';
const config = require("../../../_helpers/configServizi"); 

const ApplePayShift4 = ({ 
    onPaymentSuccess,
    onPaymentError
}) => {
    const [isApplePaySupported, setIsApplePaySupported] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const shift4Ref = useRef(null);
    const applePayButtonRef = useRef(null);

    const context = useContext(Context);
    
    // Recupera i dati dal contesto
    const publicKey = config[context.servizio]?.shift4PublicKey || "pk_test_8b5vbmqZrCf0Uih3iQLvL6SR";
    const amount = context.selectedPlan?.price || context.amount || "1.00";
    const currency = context.selectedPlan?.currency || context.currency || "USD";
    const label = context.servizio || "Gate Dating";
    const countryCode = context.countryCode || "US";

    useEffect(() => {
        // [1] Load Shift4.js library
        const loadShift4Script = () => {
            return new Promise((resolve, reject) => {
                if (window.Shift4) {
                    resolve(window.Shift4);
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://js.dev.shift4.com/shift4.js';
                script.async = true;
                script.onload = () => resolve(window.Shift4);
                script.onerror = () => reject(new Error('Failed to load Shift4.js'));
                document.body.appendChild(script);
            });
        };

        // Load Apple Pay SDK
        const loadApplePayScript = () => {
            return new Promise((resolve, reject) => {
                if (document.querySelector('script[src*="apple-pay-sdk.js"]')) {
                    resolve();
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
                script.crossOrigin = 'anonymous';
                script.async = true;
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('Failed to load Apple Pay SDK'));
                document.body.appendChild(script);
            });
        };

        const initializeApplePay = async () => {
            try {
                // Load both scripts
                await Promise.all([loadShift4Script(), loadApplePayScript()]);

                // [2] Setup Shift4.js with public key
                shift4Ref.current = window.Shift4(publicKey);

                // [3] Detect if Apple Pay is supported
                const paymentRequest = createPaymentRequest();
                const canMakePayment = await paymentRequest.canMakePayment();

                if (canMakePayment) {
                    setIsApplePaySupported(true);
                } else {
                    setError('Apple Pay is not supported in this browser.');
                }
            } catch (err) {
                console.error('Error initializing Apple Pay:', err);
                setError(err.message || 'Failed to initialize Apple Pay');
            } finally {
                setIsLoading(false);
            }
        };

        initializeApplePay();
    }, [publicKey]);

    const createPaymentRequest = () => {
        // [4a] Configure PaymentRequest method details
        const applePayMethodData = {
            supportedMethods: 'https://apple.com/apple-pay',
            data: {
                countryCode: countryCode
            }
        };

        // [4b] Configure shopping cart details
        const shoppingCartDetails = {
            total: {
                label: label,
                amount: {
                    currency: currency,
                    value: amount
                }
            }
        };

        // [4c] Create a PaymentRequest using shift4.js
        return shift4Ref.current.createPaymentRequest([applePayMethodData], shoppingCartDetails);
    };

    const processPayment = async (paymentResponse) => {
        const optionalTokenDetails = {}; // Add optional data to the token as needed
        
        try {
            // [5a] Create Shift4 token
            const token = await shift4Ref.current.createToken(paymentResponse, optionalTokenDetails);
            
            // [5b] Create charge - call backend endpoint
            const response = await fetch('/payment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: token.id })
            });

            if (!response.ok) {
                throw new Error('Payment failed');
            }

            const charge = await response.json();
            
            // [5c] Handle successful payment
            if (onPaymentSuccess) {
                onPaymentSuccess(charge);
            }

            return charge;
        } catch (err) {
            console.error('Payment processing error:', err);
            setError(err.message || 'Payment processing failed');
            
            if (onPaymentError) {
                onPaymentError(err);
            }
            
            throw err;
        }
    };

    const handleApplePayClick = async () => {
        try {
            setError(null);
            
            // [4] Create payment request
            const paymentRequest = createPaymentRequest();
            
            // [4d] Show payment sheet
            const paymentResponse = await paymentRequest.show();
            
            // [5] Proceed with payment on user verification
            await processPayment(paymentResponse);
        } catch (err) {
            console.error('Apple Pay error:', err);
            setError(err.message || 'Apple Pay payment failed');
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-4">
                <p>Loading Apple Pay...</p>
            </div>
        );
    }

    if (error && !isApplePaySupported) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    useEffect(() => {
        // Aggiungi event listener al bottone Apple Pay quando è supportato
        if (isApplePaySupported && applePayButtonRef.current) {
            const button = applePayButtonRef.current;
            button.addEventListener('click', handleApplePayClick);
            
            return () => {
                button.removeEventListener('click', handleApplePayClick);
            };
        }
    }, [isApplePaySupported]);

    return (
        <div>
            {error && (
                <div className="alert alert-danger mb-4" role="alert">
                    {error}
                </div>
            )}

            <form id="payment-form" className="grid grid-cols-1 gap-4">
                <div className="mt-4">
                    {isApplePaySupported && (
                        <apple-pay-button 
                            ref={applePayButtonRef}
                            id="apple-pay-submit-button" 
                            buttonstyle="black"
                            type="buy" 
                            locale="en-US" 
                            style={{ width: '100%', height: '48px' }}
                        />
                    )}
                </div>
            </form>
        </div>
    );
};

export default ApplePayShift4;